/**
 * Centralized URL construction utilities
 *
 * Ensures consistent URL generation across the application,
 * working seamlessly in development and production environments.
 *
 * Production config: REACT_APP_API_URL='' (empty string)
 * This produces relative URLs that work with Traefik/Coolify reverse proxy
 */

/**
 * Get the API base URL for making API requests
 * Always returns '/api' for relative path compatibility
 */
export const getApiBaseUrl = (): string => {
  return '/api';
};

/**
 * Get the application base URL (dynamic, based on current domain)
 * Returns the current origin (e.g., 'https://qrcode.lab-numihfrance.fr' in production)
 */
export const getAppBaseUrl = (): string => {
  return window.location.origin;
};

/**
 * Get the full URL for QR code redirect shortcodes
 * @param shortcode - The shortcode identifier
 * @returns Full URL like 'https://qrcode.lab-numihfrance.fr/r/ABC123'
 */
export const getRedirectUrl = (shortcode: string): string => {
  return `${getAppBaseUrl()}/r/${shortcode}`;
};

/**
 * Get the full preview URL for QR code shortcodes
 * @param shortcode - The shortcode identifier
 * @returns Full URL like 'https://qrcode.lab-numihfrance.fr/r/ABC123/preview'
 */
export const getPreviewUrl = (shortcode: string): string => {
  return `${getAppBaseUrl()}/r/${shortcode}/preview`;
};

/**
 * Get the base URL for uploaded assets (logos, images, etc.)
 * @param path - Relative path to the asset (should start with /)
 * @returns Full URL like 'https://qrcode.lab-numihfrance.fr/uploads/logo.png'
 */
export const getAssetUrl = (path: string): string => {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${getAppBaseUrl()}${normalizedPath}`;
};
